// Reports — prototype-faithful (chitos-prototype screens3.jsx). Real data via
// orpc.reports.{cvum,monthly_collection,channel_split,defaulter_rate}. The
// prototype's 5-KPI strip (foreman commission, write-offs, net margin) +
// ticket-health breakdown (prized/pending) need metrics the backend doesn't
// compute yet — shown with what we have, not faked.

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr } from '../lib/format.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';

interface MonthlyRow { due_month: string; expected_paise: number; collected_paise: number }
interface ChannelRow { method: string; total_paise: number; count: number }

const CHANNEL_COLOR: Record<string, string> = {
  upi: 'var(--primary)',
  cash: 'var(--good)',
  cheque: 'var(--warn)',
  neft: 'var(--info)',
  bank_transfer: 'var(--ink-3)',
};

const TA_MONTHS: Record<string, string> = {
  '01': 'ஜன', '02': 'பிப்', '03': 'மார்', '04': 'ஏப்', '05': 'மே', '06': 'ஜூன்',
  '07': 'ஜூ', '08': 'ஆக', '09': 'செப்', '10': 'அக்', '11': 'நவ', '12': 'டிச',
};
const EN_MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function month_label(m: string, lang: string): string {
  const mm = m.slice(5);
  if (lang === 'ta' && TA_MONTHS[mm]) return TA_MONTHS[mm];
  return EN_MONTHS[Number(mm)] ?? mm;
}

export function ReportsPage(): ReactElement {
  const lang = current_language();
  const [preview_html, set_preview_html] = useState<string | null>(null);
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);

  const cvum_q = useQuery({ queryKey: ['report-cvum'], queryFn: () => orpc.reports.cvum({}) });
  const monthly_q = useQuery({ queryKey: ['report-monthly'], queryFn: () => orpc.reports.monthly_collection({}) });
  const channel_q = useQuery({ queryKey: ['report-channel'], queryFn: () => orpc.reports.channel_split({}) });
  const defaulter_q = useQuery({ queryKey: ['report-defaulter'], queryFn: () => orpc.reports.defaulter_rate({}) });

  const pdf_m = useMutation({
    mutationFn: () => orpc.reports.download_pdf({}),
    onSuccess: (data) => {
      if (data.preview_html) set_preview_html(data.preview_html);
      else window.open(data.pdf_url, '_blank');
    },
  });

  const monthly = (monthly_q.data?.months ?? []) as MonthlyRow[];
  const channels = (channel_q.data?.by_method ?? []) as ChannelRow[];
  const channel_total = channel_q.data?.total_paise ?? 0;
  const cvum = cvum_q.data;
  const def = defaulter_q.data;

  const max_month = Math.max(1, ...monthly.flatMap((m) => [m.expected_paise, m.collected_paise]));

  const export_csv = (): void => {
    const lines = [
      'month,expected_rupees,collected_rupees,collection_rate',
      ...monthly.map(
        (m) =>
          `${m.due_month},${(m.expected_paise / 100).toFixed(2)},${(m.collected_paise / 100).toFixed(2)},${m.expected_paise > 0 ? (m.collected_paise / m.expected_paise).toFixed(3) : '0'}`,
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chitos-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Donut arcs (prototype 42-viewBox technique) over real channel data.
  let cum = 0;
  const arcs = channels.map((c) => {
    const pct = channel_total > 0 ? (c.total_paise / channel_total) * 100 : 0;
    const start = cum;
    cum += pct;
    return { method: c.method, pct, start, color: CHANNEL_COLOR[c.method] ?? 'var(--ink-3)' };
  });
  const top_channel = [...channels].sort((a, b) => b.total_paise - a.total_paise)[0];
  const top_pct = top_channel && channel_total > 0 ? Math.round((top_channel.total_paise / channel_total) * 100) : 0;

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('அறிக்கைகள்', 'Reports')}</div>
          <div className="h-sub">{t('நிதியாண்டு கண்ணோட்டம்', 'Financial overview')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={export_csv}><Icon.download /> CSV</button>
          <button className="btn primary" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : t('PDF ஏற்றுமதி', 'Export PDF')}
          </button>
        </div>
      </div>

      {/* KPIs — only the metrics the backend actually computes */}
      <div className="kpis" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="kpi primary">
          <div className="kpi-label">CVUM</div>
          <div className="kpi-value">{cvum ? format_inr(cvum.total_pool_paise) : '—'}</div>
          <div className="kpi-sub">{cvum ? `${cvum.active_chits} ${t('சீட்டுகள்', 'chits')} · ${cvum.total_subscribers} ${t('பேர்', 'subs')}` : ''}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t('மாத ஓட்ட விகிதம்', 'Monthly run-rate')}</div>
          <div className="kpi-value">{cvum ? format_inr(cvum.monthly_run_rate_paise) : '—'}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">{t('செயலில் சீட்டுகள்', 'Active chits')}</div>
          <div className="kpi-value">{cvum ? `${cvum.active_chits}` : '—'}</div>
        </div>
        <div className={'kpi' + ((def?.rate ?? 0) > 0.1 ? ' warn' : '')}>
          <div className="kpi-label">{t('தாமத விகிதம்', 'Defaulter rate')}</div>
          <div className="kpi-value">{def ? `${Math.round(def.rate * 100)}%` : '—'}</div>
          <div className="kpi-sub">{def ? `${def.overdue_subscribers} / ${def.active_subscribers}` : ''}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 14 }}>
        {/* Monthly collection vs expected — prototype horizontal bars */}
        <div className="card">
          <div className="card-head">
            <div className="card-title">{t('மாதாந்திர வசூல் vs எதிர்பார்ப்பு', 'Monthly collection vs expected')}</div>
            <div className="card-sub mono">▎{t('எதிர்பார்ப்பு குறி', 'expected marker')}</div>
          </div>
          <div style={{ padding: '14px 18px' }}>
            {monthly.length === 0 && (
              <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>{t('தரவு இல்லை', 'No data yet')}</div>
            )}
            {monthly.map((m) => (
              <div key={m.due_month} className="chart-bar">
                <span className="lbl">{month_label(m.due_month, lang)}</span>
                <div className="bar">
                  <span className="actual" style={{ width: (m.collected_paise / max_month) * 100 + '%' }} />
                  <span className="target" style={{ left: (m.expected_paise / max_month) * 100 + '%' }} />
                </div>
                <span className="val">
                  {format_inr(m.collected_paise)}
                  <span style={{ color: 'var(--ink-3)', fontSize: 10 }}> / {format_inr(m.expected_paise)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment channels donut */}
        <div className="card">
          <div className="card-head">
            <div className="card-title">{t('செலுத்தல் வழிகள்', 'Payment channels')}</div>
          </div>
          <div style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 18 }}>
            {channel_total === 0 ? (
              <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>{t('செலுத்தல் இல்லை', 'No payments yet')}</div>
            ) : (
              <>
                <svg className="donut" viewBox="0 0 42 42">
                  {arcs.map((a, i) => (
                    <circle
                      key={i}
                      cx="21"
                      cy="21"
                      r="15.915"
                      fill="transparent"
                      stroke={a.color}
                      strokeWidth="6"
                      strokeDasharray={`${a.pct} ${100 - a.pct}`}
                      strokeDashoffset={100 - a.start}
                      transform="rotate(-90 21 21)"
                    />
                  ))}
                  <text x="21" y="22" textAnchor="middle" fontSize="6" fontWeight="600" fill="var(--ink)">{top_pct}%</text>
                  <text x="21" y="27" textAnchor="middle" fontSize="3" fill="var(--ink-3)">
                    {(top_channel?.method ?? '').toUpperCase()}
                  </text>
                </svg>
                <div style={{ flex: 1 }}>
                  {channels.map((c) => (
                    <div key={c.method} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: CHANNEL_COLOR[c.method] ?? 'var(--ink-3)', flexShrink: 0 }} />
                      <span style={{ flex: 1, textTransform: 'uppercase' }} className="mono">{c.method}</span>
                      <span className="mono">{Math.round((c.total_paise / channel_total) * 100)}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Member health — what defaulter_rate gives us */}
      <div className="section-band">{t('உறுப்பினர் நிலை', 'Member health')}</div>
      <div className="stat-grid">
        <div className="stat">
          <div className="lbl">{t('செயலில் உறுப்பினர்', 'Active members')}</div>
          <div className="val">{def ? def.active_subscribers : '—'}</div>
        </div>
        <div className="stat" style={{ background: 'var(--warn-soft)', borderColor: '#E8D49A' }}>
          <div className="lbl">{t('தாமதம்', 'Overdue')}</div>
          <div className="val">{def ? def.overdue_subscribers : '—'}</div>
        </div>
        <div className="stat">
          <div className="lbl">{t('தாமத விகிதம்', 'Defaulter rate')}</div>
          <div className="val">{def ? `${Math.round(def.rate * 100)}%` : '—'}</div>
        </div>
      </div>

      {/* Available reports */}
      <div className="section-band">{t('கிடைக்கக்கூடிய அறிக்கைகள்', 'Available reports')}</div>
      <div className="chits-page-grid">
        {/* 1. Collection Day Sheet */}
        <Link to="/reports/collection-day" className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--primary-soft)', color: 'var(--primary)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.people />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('வசூல் நாள் பட்டியல்', 'Collection Day Sheet')}</div>
            <div className="chit-id">{t('இன்றைய வசூல் — ஃபோர்மேன் சுற்றுக்கு', "Today's dues — for the foreman's round")}</div>
          </div>
        </Link>

        {/* 2. Aging Report */}
        <Link to="/reports/aging" className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--warn-soft)', color: 'var(--warn)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.bell />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('வயது அறிக்கை', 'Aging Report')}</div>
            <div className="chit-id">{t('நிலுவை — 0-15 / 16-30 / 31-60 / 60+', 'Overdue — 0-15 / 16-30 / 31-60 / 60+')}</div>
          </div>
        </Link>

        {/* 3. Day Book */}
        <Link to="/reports/day-book" className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--good-soft)', color: 'var(--good)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.cash />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('தினசரி கணக்கு', 'Day Book')}</div>
            <div className="chit-id">{t('தொடக்க + வசூல் + வழங்கல் + இறுதி இருப்பு', 'Opening + receipts + payouts + closing')}</div>
          </div>
        </Link>

        {/* 4. Foreman Commission */}
        <Link to="/reports/foreman-commission" className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--info-soft)', color: 'var(--info)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.reports />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('ஃபோர்மேன் கமிஷன்', 'Foreman Commission')}</div>
            <div className="chit-id">{t('நிதியாண்டு வாரியாக சீட்டுவாரியாக', 'Per chit per month, FY scoped')}</div>
          </div>
        </Link>

        {/* 5. Member Statement */}
        <Link to="/reports/member-statement" className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--primary-soft)', color: 'var(--primary)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.doc />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('உறுப்பினர் அறிக்கை', 'Member Statement')}</div>
            <div className="chit-id">{t('எல்லா சீட்டுகளும் — ஒரு PDF-ல்', 'All chits in one PDF — per member')}</div>
          </div>
        </Link>

        {/* 6. Chit P&L */}
        <Link to="/reports/chit-pnl" className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--good-soft)', color: 'var(--good)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.chits />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('சீட்டு லாபம்/நஷ்டம்', 'Chit P&L')}</div>
            <div className="chit-id">{t('ஒரு சீட்டின் வாழ்நாள் கணக்கு', 'Lifetime financials for one chit')}</div>
          </div>
        </Link>

        {/* 7. Payout Forecast */}
        <Link to="/reports/payout-forecast" className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--warn-soft)', color: 'var(--warn)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.gavel />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('பணம் வழங்கல் கணிப்பு', 'Payout Forecast')}</div>
            <div className="chit-id">{t('30/60/90 நாள் தேவையான பணம்', '30 / 60 / 90 day cashflow forecast')}</div>
          </div>
        </Link>

        {/* 8. Comms Analytics */}
        <Link to="/reports/comms" className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--info-soft)', color: 'var(--info)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.wa />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('தொடர்பு பகுப்பாய்வு', 'Comms Analytics')}</div>
            <div className="chit-id">{t('வாட்ஸ்அப் · மின்னஞ்சல் · குரல்', 'WhatsApp · email · voice — by date')}</div>
          </div>
        </Link>

        {/* 9. Overview snapshot (compresses the previous PDF + CSV pair) */}
        <div className="chit-card-2" style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--bg-elev)', color: 'var(--ink-3)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Icon.reports />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className={'chit-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('கண்ணோட்ட பதிவு', 'Overview snapshot')}</div>
            <div className="chit-id">{t('இந்த பக்கத்தின் KPI + வரைபடம்', "This page's KPIs + charts")}</div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" className="btn sm" onClick={export_csv} title="CSV"><Icon.download /></button>
            <button type="button" className="btn sm primary" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending} title="PDF"><Icon.download /></button>
          </div>
        </div>
      </div>

      {preview_html && (
        <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm p-4 overflow-auto">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
            <div className="flex justify-between items-center px-4 py-2 border-b border-line bg-bg-elev">
              <div className="text-sm font-medium">PDF preview (DRY_RUN)</div>
              <button onClick={() => set_preview_html(null)} className="text-xs text-ink/60 hover:text-bad">Close</button>
            </div>
            <iframe title="report-preview" srcDoc={preview_html} className="w-full" style={{ height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
